import {
  Autocomplete,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";
import InputField from "components/Input/InputField";
import Loader from "components/Loader";
import DeleteModal from "components/Modals/DeleteModal";
import SearchField from "components/SearchField";
import { deleteData } from "const/axios";
import { API_URL } from "const/constants";
import useKothar from "context/useKothar";
import { useEffect, useMemo, useState } from "react";
import { AiFillEdit } from "react-icons/ai";
import { FaChevronUp, FaPlusCircle } from "react-icons/fa";
import {
  FaBuilding,
  FaEnvelope,
  FaGlobe,
  FaMapMarkerAlt,
  FaPhone,
  FaUser,
  FaUsers,
} from "react-icons/fa";
import { IoArrowBack } from "react-icons/io5";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { canAssignOrgSuperAdmin, getUserType } from "utils/permissionUtils";
import { getRoleForOrg, userHasOrgMembership } from "utils/userMembershipUtils";
import UsersList from "./UsersList";

const USER_TYPE_OPTIONS = [
  { label: "SUPER_ADMIN", value: "SUPER_ADMIN" },
  { label: "ADMIN", value: "ADMIN" },
  { label: "ACCOUNT", value: "ACCOUNT" },
  { label: "USER", value: "USER" },
];

const DetailItem = ({ icon: Icon, label, children }) => (
  <div className="rounded-lg border border-app-border bg-app-surface p-4">
    <div className="flex items-start gap-3">
      {Icon && (
        <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-app-surface-muted text-app-text-secondary">
          <Icon className="h-4 w-4" />
        </span>
      )}
      <div className="min-w-0 flex-1">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-app-text-muted">
          {label}
        </p>
        <div className="mt-1 text-sm font-medium text-app-text break-words">{children || "—"}</div>
      </div>
    </div>
  </div>
);

const StatCard = ({ label, value, sub }) => (
  <div className="rounded-lg border border-app-border bg-app-surface px-4 py-3">
    <p className="text-[11px] font-semibold uppercase tracking-wide text-app-text-muted">{label}</p>
    <p className="mt-1 text-2xl font-semibold text-app-text tabular-nums">{value}</p>
    {sub ? <p className="mt-0.5 text-xs text-app-text-secondary">{sub}</p> : null}
  </div>
);

const ViewOrganisation = () => {
  const isSuperAdmin = getUserType() === "SUPER_ADMIN";
  const canAssignSuperAdmin = canAssignOrgSuperAdmin();
  const { id } = useParams();
  const orgId = id ? parseInt(id, 10) : null;
  const navigate = useNavigate();
  const [consultancy, setConsultancy] = useState(null);
  const [showAddUserForm, setShowAddUserForm] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [openConfirmationModal, setOpenConfirmationModal] = useState({});
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [assignSuperAdminOpen, setAssignSuperAdminOpen] = useState(false);
  const [assignSuperAdminTarget, setAssignSuperAdminTarget] = useState(null);
  const [{ usersList, token }, { refetchUsers }] = useKothar();

  const [userFormData, setUserFormData] = useState({
    name: "",
    mobileNumber: "",
    email: "",
    username: "",
    type: "",
    accessToDiscussion: true,
    orgId: id ? parseInt(id) : null,
    permittedModules: [],
  });

  const { data: consultancyData, isFetching: isConsultancyFetching } = useQuery({
    queryKey: ["consultancy", id],
    queryFn: async () => {
      const response = await axios.get(`${API_URL}/organization/${id}`);
      return response.data?.data;
    },
    enabled: !!id,
  });

  useEffect(() => {
    if (consultancyData) {
      setConsultancy(consultancyData);
    }
  }, [consultancyData]);

  // Filter users by active membership in this organisation
  useEffect(() => {
    if (usersList && id) {
      const parsedOrgId = parseInt(id, 10);
      const consultancyUsers = usersList
        .filter((user) => userHasOrgMembership(user, parsedOrgId))
        .map((user) => ({
          ...user,
          type: getRoleForOrg(user, parsedOrgId) || user?.type,
          orgId: parsedOrgId,
          membershipId:
            user?.memberships?.find((membership) => membership.orgId === parsedOrgId)
              ?.membershipId || user?.membershipId,
        }));

      if (searchText.length > 0) {
        const term = searchText.toLowerCase();
        const filtered = consultancyUsers.filter(
          (user) =>
            user?.name?.toLowerCase().includes(term) ||
            user?.email?.toLowerCase().includes(term) ||
            user?.username?.toLowerCase().includes(term) ||
            user?.mobileNumber?.toLowerCase().includes(term)
        );
        setFilteredUsers(filtered);
      } else {
        setFilteredUsers(consultancyUsers);
      }
    }
  }, [usersList, id, searchText]);

  const userStats = useMemo(() => {
    const verified = filteredUsers.filter((u) => u?.emailVerified === "Y").length;
    return {
      total: filteredUsers.length,
      verified,
      pending: filteredUsers.length - verified,
    };
  }, [filteredUsers]);

  const { isLoading: isResendingInvite, mutate: resendInvite } = useMutation(
    async (userId) => {
      await axios.post(`${API_URL}/users/${userId}/resend-invite`);
    },
    {
      onSuccess() {
        toast.success("Invitation email resent");
        refetchUsers();
      },
      onError(err) {
        toast.error(err?.response?.data?.message || "Failed to resend invitation email");
      },
    }
  );

  useEffect(() => {
    if (!usersList?.length) {
      refetchUsers();
    }
  }, []);

  const { isLoading: isSubmittingUser, mutate: addUser } = useMutation(
    async (payload) => {
      const response = await axios.post(`${API_URL}/register/user`, payload);
      return response.data;
    },
    {
      onSuccess(response) {
        toast.success(response?.message || "User added successfully");
        setUserFormData({
          name: "",
          mobileNumber: "",
          email: "",
          username: "",
          type: "",
          accessToDiscussion: true,
          orgId: id ? parseInt(id) : null,
          permittedModules: [],
        });
        setShowAddUserForm(false);
        refetchUsers();
      },
      onError(err) {
        toast.error(err?.response?.data?.errorMessage || "Error adding user");
      },
    }
  );

  const { isLoading: isAssigningSuperAdmin, mutate: assignSuperAdmin } = useMutation(
    async (userId) => {
      const response = await axios.post(`${API_URL}/organization/${orgId}/superadmins`, {
        userId,
      });
      return response.data;
    },
    {
      onSuccess() {
        toast.success("Super admin assigned successfully");
        setAssignSuperAdminOpen(false);
        setAssignSuperAdminTarget(null);
        refetchUsers();
      },
      onError(err) {
        toast.error(err?.response?.data?.errorMessage || "Failed to assign super admin");
      },
    }
  );

  const nonMemberUsers = useMemo(() => {
    if (!usersList || orgId == null) return [];
    return usersList.filter((user) => !userHasOrgMembership(user, orgId));
  }, [usersList, orgId]);

  const handleUserInputChange = (e) => {
    const { name, value } = e.target;
    setUserFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddUser = (e) => {
    e.preventDefault();
    addUser({
      name: userFormData?.name,
      username: userFormData?.username,
      email: userFormData?.email,
      mobileNumber: userFormData?.mobileNumber,
      type: userFormData?.type,
      orgId: userFormData?.orgId,
      accessToDiscussion: userFormData?.accessToDiscussion,
    });
  };

  const deleteUser = () => {
    deleteData(
      `${API_URL}/users/${openConfirmationModal?.id}`,
      () => {
        setOpenConfirmationModal({ state: false, id: null });
        refetchUsers();
      },
      token
    );
  };

  if (isConsultancyFetching) {
    return (
      <div className="flex h-[60vh] items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (!consultancy) {
    return (
      <div className="flex h-[60vh] flex-col items-center justify-center gap-4 px-4">
        <p className="text-base text-app-text-secondary">Organization not found</p>
        <Button variant="outlined" onClick={() => navigate(-1)}>
          Go back
        </Button>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-6">
      {/* Page header */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-app-border bg-app-surface text-app-text-secondary transition hover:bg-app-surface-muted"
            aria-label="Go back"
          >
            <IoArrowBack className="h-5 w-5" />
          </button>
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wide text-app-text-muted">
              Organization
            </p>
            <h1 className="truncate text-xl font-semibold text-app-text sm:text-2xl">
              {consultancy?.name || "Organization Details"}
            </h1>
            {consultancy?.owner && (
              <p className="mt-0.5 text-sm text-app-text-secondary">Owner: {consultancy.owner}</p>
            )}
          </div>
        </div>
        <Button
          variant="outlined"
          startIcon={<AiFillEdit />}
          onClick={() =>
            navigate("/admin/consultancy/add", {
              state: { item: consultancy },
            })
          }
          className="shrink-0 self-start sm:self-auto"
        >
          Edit organization
        </Button>
      </div>

      {/* Stats */}
      <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <StatCard label="Total users" value={userStats.total} sub="In this organization" />
        <StatCard label="Verified" value={userStats.verified} sub="Email confirmed" />
        <StatCard
          label="Pending verification"
          value={userStats.pending}
          sub="Awaiting email confirm"
        />
      </div>

      {/* Organization profile */}
      <section className="mb-6 overflow-hidden rounded-xl border border-app-border bg-app-surface shadow-sm">
        <div className="border-b border-app-border px-4 py-3 sm:px-6">
          <h2 className="text-sm font-semibold text-app-text">Organization profile</h2>
          <p className="text-xs text-app-text-muted">Contact details and business information</p>
        </div>

        <div className="p-4 sm:p-6">
          {(consultancy?.logo || consultancy?.image) && (
            <div className="mb-6 flex flex-wrap gap-6">
              {consultancy?.logo && (
                <div>
                  <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-app-text-muted">
                    Logo
                  </p>
                  <img
                    src={consultancy.logo}
                    alt="Organization logo"
                    className="h-24 w-24 rounded-lg border border-app-border bg-white object-contain p-2"
                  />
                </div>
              )}
              {consultancy?.image && (
                <div>
                  <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-app-text-muted">
                    Image
                  </p>
                  <img
                    src={consultancy.image}
                    alt="Organization"
                    className="h-24 w-24 rounded-lg border border-app-border bg-white object-contain p-2"
                  />
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <DetailItem icon={FaBuilding} label="Name">
              {consultancy?.name}
            </DetailItem>
            <DetailItem icon={FaUser} label="Owner name">
              {consultancy?.owner}
            </DetailItem>
            <DetailItem icon={FaEnvelope} label="Email">
              {consultancy?.email ? (
                <a href={`mailto:${consultancy.email}`} className="text-primary hover:underline">
                  {consultancy.email}
                </a>
              ) : null}
            </DetailItem>
            <DetailItem icon={FaGlobe} label="Website">
              {consultancy?.website ? (
                <a
                  href={consultancy.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline break-all"
                >
                  {consultancy.website}
                </a>
              ) : null}
            </DetailItem>
            <DetailItem icon={FaMapMarkerAlt} label="Address">
              {consultancy?.address}
            </DetailItem>
            <DetailItem icon={FaBuilding} label="PAN / ABN number">
              {consultancy?.abn || consultancy?.panNumber}
            </DetailItem>
            <DetailItem icon={FaPhone} label="Primary contact">
              {consultancy?.primaryContactNumber}
            </DetailItem>
            <DetailItem icon={FaPhone} label="Secondary contact">
              {consultancy?.secondaryContactNumber}
            </DetailItem>
          </div>
        </div>
      </section>

      {/* Users */}
      <section className="overflow-hidden rounded-xl border border-app-border bg-app-surface shadow-sm">
        <div className="flex flex-col gap-3 border-b border-app-border px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <div>
            <h2 className="flex items-center gap-2 text-sm font-semibold text-app-text">
              <FaUsers className="h-4 w-4 text-app-text-muted" />
              Users
              <span className="rounded-full bg-app-surface-muted px-2 py-0.5 text-xs font-medium text-app-text-secondary">
                {filteredUsers?.length || 0}
              </span>
            </h2>
            <p className="text-xs text-app-text-muted">Manage organization members</p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <SearchField type="User" searchText={searchText} setSearchText={setSearchText} />
            {canAssignSuperAdmin && (
              <Button
                variant="outlined"
                onClick={() => setAssignSuperAdminOpen(true)}
                className="whitespace-nowrap"
              >
                Assign super admin
              </Button>
            )}
            <Button
              variant="contained"
              startIcon={showAddUserForm ? <FaChevronUp /> : <FaPlusCircle />}
              onClick={() => setShowAddUserForm(!showAddUserForm)}
              className="whitespace-nowrap"
            >
              {showAddUserForm ? "Hide form" : "Add user"}
            </Button>
          </div>
        </div>

        <Collapse in={showAddUserForm}>
          <div className="border-b border-app-border bg-app-surface-muted px-4 py-5 sm:px-6">
            <h3 className="mb-4 text-sm font-semibold text-app-text">Add new user</h3>
            <form onSubmit={handleAddUser} className="space-y-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <InputField
                  label="Name"
                  placeholder="Full name"
                  name="name"
                  required
                  type="text"
                  value={userFormData?.name}
                  onChange={handleUserInputChange}
                />
                <InputField
                  label="Username"
                  placeholder="Username"
                  name="username"
                  required
                  type="text"
                  value={userFormData?.username}
                  onChange={handleUserInputChange}
                />
                <InputField
                  type="email"
                  placeholder="Email"
                  name="email"
                  label="Email"
                  required
                  value={userFormData?.email}
                  onChange={handleUserInputChange}
                />
                <InputField
                  type="number"
                  placeholder="Mobile number"
                  name="mobileNumber"
                  label="Mobile number"
                  required
                  value={userFormData?.mobileNumber}
                  onChange={handleUserInputChange}
                />
                <div className="relative w-full md:col-span-2">
                  <label className="mb-2 block text-xs font-bold uppercase text-app-text-secondary">
                    User type
                  </label>
                  <Autocomplete
                    disablePortal
                    size="small"
                    options={USER_TYPE_OPTIONS}
                    value={
                      USER_TYPE_OPTIONS.find((item) => item?.value === userFormData?.type) || null
                    }
                    renderInput={(params) => <TextField {...params} label="User type" required />}
                    isOptionEqualToValue={(options, value) => options.value === value.value}
                    onChange={(e, value) => {
                      setUserFormData((prev) => ({
                        ...prev,
                        type: value?.value,
                      }));
                    }}
                  />
                </div>
                <div className="md:col-span-2 rounded-lg border border-app-border bg-app-surface px-4 py-3">
                  <p className="text-sm text-app-text-secondary">
                    A temporary password will be auto-generated and sent to the user&apos;s email.
                    They will be prompted to set a new password on first login.
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-1">
                <Button variant="outlined" onClick={() => setShowAddUserForm(false)}>
                  Cancel
                </Button>
                <Button variant="contained" type="submit" disabled={isSubmittingUser}>
                  {isSubmittingUser ? "Adding..." : "Add user"}
                </Button>
              </div>
            </form>
          </div>
        </Collapse>

        <UsersList
          filteredUsers={filteredUsers}
          navigate={navigate}
          setOpenConfirmationModal={setOpenConfirmationModal}
          searchText={searchText}
          resendInvite={resendInvite}
          isResendingInvite={isResendingInvite}
          canResend={isSuperAdmin}
        />
      </section>

      {openConfirmationModal.state && (
        <DeleteModal
          open={openConfirmationModal.state}
          item="User"
          handleCancel={() => setOpenConfirmationModal({ state: false, id: null })}
          handleDelete={deleteUser}
        />
      )}

      <Dialog
        open={assignSuperAdminOpen}
        onClose={() => {
          setAssignSuperAdminOpen(false);
          setAssignSuperAdminTarget(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Assign super admin</DialogTitle>
        <DialogContent>
          <p className="mb-4 text-sm text-app-text-secondary">
            Pick an existing user to make SUPER_ADMIN of {consultancy?.name || "this organization"}.
            They don&apos;t need to already be a member.
          </p>
          <Autocomplete
            options={nonMemberUsers}
            getOptionLabel={(user) =>
              `${user?.name || user?.username || "—"} (${user?.email || "—"})`
            }
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            value={assignSuperAdminTarget}
            onChange={(e, value) => setAssignSuperAdminTarget(value)}
            renderInput={(params) => (
              <TextField {...params} label="User" placeholder="Search by name or email" />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setAssignSuperAdminOpen(false);
              setAssignSuperAdminTarget(null);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!assignSuperAdminTarget || isAssigningSuperAdmin}
            onClick={() => assignSuperAdmin(assignSuperAdminTarget.id)}
          >
            {isAssigningSuperAdmin ? "Assigning..." : "Assign"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ViewOrganisation;
